import React, { useState, useMemo, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link, router, usePage } from '@inertiajs/react'
import useTranslation from '../hooks/useTranslation'

export default function TextReader({ text, previous_score, completion_count, user_locale }) {
  const { t } = useTranslation()
  const { auth } = usePage().props
  const isAdmin = auth?.user?.role === 'admin'
  const [revealedTokenIds, setRevealedTokenIds] = useState(new Set())
  const [revealedSpanIds, setRevealedSpanIds] = useState(new Set())
  const [activeTooltip, setActiveTooltip] = useState(null) // { type: 'span'|'token', spanId?, tokenId?, anchorTokenId? }
  const [penaltyCount, setPenaltyCount] = useState(0)
  const [isQuizOpen, setIsQuizOpen] = useState(false)
  const [readingTime, setReadingTime] = useState(0)
  const userLocale = user_locale || auth?.user?.native_language || 'en'
  
  // Track reading time
  useEffect(() => {
    const startTime = Date.now()
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000) // seconds
      setReadingTime(elapsed)
    }, 1000)
    
    return () => clearInterval(interval)
  }, [])
  
  const formatReadingTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds}s`
    }
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
  }

  const spans = text.spans || []

  const spanByTokenId = useMemo(() => {
    const map = new Map()
    for (const span of spans) {
      for (const tokenId of span.token_ids || []) {
        // v1: non-overlapping, so first wins
        if (!map.has(tokenId)) map.set(tokenId, span)
      }
    }
    return map
  }, [spans])

  const handleTokenClick = (token) => {
    if (token.p) return

    const span = spanByTokenId.get(token.id)
    if (span) {
      // Always show a tooltip on the clicked token, but only penalize the first reveal.
      setActiveTooltip({ type: 'span', spanId: span.id, anchorTokenId: token.id })

      if (!revealedSpanIds.has(span.id)) {
        const next = new Set(revealedSpanIds)
        next.add(span.id)
        setRevealedSpanIds(next)
        setPenaltyCount(p => p + 1)
      }
      return
    }

    setActiveTooltip({ type: 'token', tokenId: token.id })

    if (!revealedTokenIds.has(token.id)) {
      const next = new Set(revealedTokenIds)
      next.add(token.id)
      setRevealedTokenIds(next)
      setPenaltyCount(p => p + 1)
    }
  }

  return (
    <div className="flex flex-col md:flex-row h-screen overflow-hidden">
      {/* Sidebar */}
      <div className="w-full md:w-80 bg-gray-50 border-r flex flex-row md:flex-col justify-between p-6 shrink-0 z-10 shadow-sm md:shadow-none">
        <div>
          <Link href="/texts" className="text-sm text-gray-500 hover:text-gray-900 mb-4 block">&larr; {t('text_reader.back_to_library')}</Link>
          <div className="flex items-start justify-between gap-2 mb-2">
            <h1 className="text-2xl font-bold flex-1">{text.title}</h1>
            {isAdmin && (
              <Link 
                href={`/admin/texts/${text.id}/edit`}
                className="text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-md hover:bg-emerald-700 font-medium whitespace-nowrap"
              >
                Edit
              </Link>
            )}
          </div>
          <p className="text-gray-600 mb-6">{text.topic}</p>
          
          <div className="hidden md:block">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-4">
              <div className="text-sm text-gray-500 uppercase tracking-wide font-semibold">{t('text_reader.penalties')}</div>
              <div className="text-4xl font-bold text-rose-500 mt-1">{penaltyCount}</div>
            </div>
            
            {previous_score && (
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-4">
                <div className="text-sm text-gray-500 uppercase tracking-wide font-semibold">{t('text_reader.previous_score')}</div>
                <div className="text-2xl font-bold text-gray-800 mt-1">{previous_score}</div>
              </div>
            )}
            
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-4">
              <div className="text-sm text-gray-500 uppercase tracking-wide font-semibold">{t('text_reader.reading_time')}</div>
              <div className="text-2xl font-bold text-gray-800 mt-1">{formatReadingTime(readingTime)}</div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <div className="text-sm text-gray-500 uppercase tracking-wide font-semibold">{t('text_reader.completion_count')}</div>
              <div className="text-2xl font-bold text-gray-800 mt-1">{completion_count || 0}</div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 md:block">
           <div className="md:hidden mr-auto">
              <span className="text-rose-500 font-bold text-xl">{penaltyCount} {t('text_reader.pts')}</span>
           </div>
           <button 
             onClick={() => setIsQuizOpen(true)}
             className="bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:bg-emerald-700 w-full md:w-auto"
           >
             {t('text_reader.im_done')}
           </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6 md:p-12 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8 rounded-xl overflow-hidden shadow-sm bg-gray-100">
             {text.image_url ? (
                <img
                  src={text.image_url}
                  alt={text.title}
                  className="w-full h-64 object-cover"
                  onError={(e) => {
                    // Avoid broken images when stored URLs expire (e.g. OpenAI signed URLs)
                    e.currentTarget.onerror = null
                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(text.title)}&background=random&size=1024`
                  }}
                />
             ) : (
                <div className="w-full h-48 bg-gradient-to-r from-emerald-50 to-teal-50 flex items-center justify-center border-b">
                  <div className="text-emerald-800 opacity-20 transform scale-150">
                    <svg className="w-24 h-24" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                    </svg>
                  </div>
                </div>
             )}
          </div>
          
          <div className="text-xl md:text-2xl leading-loose font-serif">
            {text.tokens.map(token => (
               <Token 
                 key={token.id} 
                 token={token} 
                 isRevealed={
                   token.p
                     ? false
                     : (spanByTokenId.get(token.id)
                        ? revealedSpanIds.has(spanByTokenId.get(token.id).id)
                        : revealedTokenIds.has(token.id))
                 }
                 isTooltipAnchor={
                   !token.p &&
                   ((activeTooltip?.type === 'span' && activeTooltip?.anchorTokenId === token.id) ||
                    (activeTooltip?.type === 'token' && activeTooltip?.tokenId === token.id))
                 }
                 tooltipText={
                   (() => {
                     if (token.p) return null

                     const span = spanByTokenId.get(token.id)
                     if (span && activeTooltip?.type === 'span' && activeTooltip?.spanId === span.id) {
                       const gloss = span.gloss || {}
                       return firstNonEmptyGloss(gloss, userLocale) || token.t || token.w || null
                     }

                     // Legacy fallback: per-token `t` (some older texts have this in DB).
                     if (activeTooltip?.type === 'token' && activeTooltip?.tokenId === token.id) {
                       return token.t || token.w || null
                     }
                     return null
                   })()
                 }
                 onClick={() => handleTokenClick(token)}
               />
            ))}
          </div>
        </div>
      </div>

      {/* Quiz Modal */}
      <AnimatePresence>
        {isQuizOpen && (
          <QuizModal 
            questions={text.questions || []} 
            penaltyCount={penaltyCount}
            textId={text.id}
            onClose={() => setIsQuizOpen(false)} 
            t={t}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

function Token({ token, isRevealed, isTooltipAnchor, tooltipText, onClick }) {
  if (token.p) {
    return <span className="mr-1">{token.w}</span>
  }

  return (
    <span 
      onClick={onClick}
      className={`relative inline-block cursor-pointer px-1 mx-0.5 rounded transition-all duration-200 select-none ${
        isRevealed 
          ? 'bg-amber-100 text-gray-900' 
          : 'hover:bg-gray-100 hover:text-gray-600'
      }`}
    >
      {token.w}
      <AnimatePresence>
        {isRevealed && isTooltipAnchor && tooltipText && (
          <motion.div
            initial={{ opacity: 0, y: 5, scale: 0.9 }}
            animate={{ opacity: 1, y: -45, scale: 1 }}
            exit={{ opacity: 0 }}
            className="absolute left-1/2 -translate-x-1/2 bg-white border border-gray-200 shadow-xl px-3 py-1.5 rounded-lg text-sm font-sans whitespace-nowrap z-50 pointer-events-none text-emerald-700 font-bold"
          >
            {tooltipText}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-8 border-transparent border-t-white"></div>
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  )
}

function firstNonEmptyGloss(gloss, preferredLocale) {
  if (!gloss) return null
  const preferred = gloss[preferredLocale]
  if (preferred && preferred.toString().trim().length > 0) return preferred
  const en = gloss.en
  if (en && en.toString().trim().length > 0) return en
  for (const k of Object.keys(gloss)) {
    const v = gloss[k]
    if (v && v.toString().trim().length > 0) return v
  }
  return null
}

function QuizModal({ questions, penaltyCount, textId, onClose, t }) {
  const [answers, setAnswers] = useState({})
  
  const handleSubmit = (e) => {
    e.preventDefault()
    router.post('/text_sessions', {
      text_id: textId,
      penalty_count: penaltyCount,
      answers: answers
    }, {
      onSuccess: () => {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'quiz_completed', {
            'text_id': textId,
            'score': penaltyCount
          })
        }
      }
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold">{t('text_reader.quiz_title')}</h2>
          <p className="text-gray-600">{t('text_reader.quiz_description')}</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {questions.length === 0 && (
            <p className="text-gray-500 italic">{t('text_reader.no_questions')}</p>
          )}

          {questions.map((q, idx) => (
            <div key={q.id}>
              <p className="font-semibold text-lg mb-3">{idx + 1}. {q.content}</p>
              <div className="space-y-2">
                <QuizOptions 
                  question={q} 
                  selected={answers[q.id]} 
                  onChange={(val) => setAnswers(prev => ({ ...prev, [q.id]: val }))} 
                />
              </div>
            </div>
          ))}
          
          <div className="flex justify-end gap-4 pt-4 border-t">
            <button 
              type="button" 
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              {t('text_reader.cancel')}
            </button>
            <button 
              type="submit"
              className="bg-emerald-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-emerald-700"
            >
              {t('text_reader.submit_finish')}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

function QuizOptions({ question, selected, onChange }) {
  const options = useMemo(() => {
    const all = [question.correct_answer, ...(question.distractors || [])]
    return all.sort(() => Math.random() - 0.5)
  }, [question.id])

  return (
    <>
      {options.map((opt, i) => (
        <label key={i} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
          <input 
            type="radio" 
            name={`q-${question.id}`} 
            value={opt}
            checked={selected === opt}
            onChange={() => onChange(opt)}
            className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-gray-700">{opt}</span>
        </label>
      ))}
    </>
  )
}
